// Shared "is this user a regular member?" predicate for booking-access decisions.
//
// A regular member is a plain 'Member' with no staff permissions. Anyone else —
// staff (isAdmin: admin / front-of-house) or a non-Member member type such as
// Coach — is a "non-regular member" and gets the privileged booking experience
// (e.g. the full end-time range rather than the two-interval member cap). The
// memberType breadth ('!== Member') is deliberate: the rule is "anyone who isn't
// a regular member". [FORT-278]
export function isNonRegularMember(isAdmin: boolean, memberType: string | undefined): boolean {
  return isAdmin || (memberType ?? 'Member') !== 'Member';
}

// Namespace prefix for the per-sport coach permission — mirrors the API's
// CoachingPermissions.Prefix. Module-local: only coachBookingPermission builds from it.
const COACH_BOOKING_PERMISSION_PREFIX = 'bookings:coach:';

// The per-sport coach permission — mirrors the API's
// CoachingPermissions.ForFacilityType (bookings:coach:<type>, lower-cased).
// A coach holding it has elevated booking privileges for that facility type.
// `facilityType` must be the FacilityType enum name (e.g. 'Squash', 'RealTennis') —
// the same value the /bookings endpoint binds as its query param — so the lower-cased
// string matches the API's. A display label with spacing/casing would silently miss.
export function coachBookingPermission(facilityType: string): string {
  return `${COACH_BOOKING_PERMISSION_PREFIX}${facilityType.toLowerCase()}`;
}

// Whether the user may place *admin* bookings (member-less, with a booking title)
// for this facility type: staff (isAdmin) OR a coach holding bookings:coach:<type>.
// The server treats an admin booking purely by shape (booked as the house member +
// NonMembers metadata), so this gate only surfaces the capability in the UI. [FORT-278]
export function canPlaceAdminBooking(
  isAdmin: boolean,
  permissionNames: string[],
  facilityType: string
): boolean {
  return isAdmin || permissionNames.includes(coachBookingPermission(facilityType));
}

// The member an admin (member-less, titled) booking is attributed to:
//   - staff book as the club — the "house" member whose name equals the client name;
//   - a coach books as *themselves*, so a coaching session is traceable to the coach
//     rather than the anonymous club account.
// The booking still carries the NonMembers flag either way, so the server treats it
// as an admin booking regardless of attribution. Returns undefined if not found.
// [FORT-278]
export function resolveAdminBookingMember<T extends { name: string; membershipId: string }>(
  isAdmin: boolean,
  members: T[] | undefined,
  currentClientName: string | undefined,
  currentMembershipId: string | undefined
): T | undefined {
  if (isAdmin) return members?.find((m) => m.name === currentClientName);
  return members?.find((m) => m.membershipId === currentMembershipId);
}
